"use client";

import { notifications } from "@mantine/notifications";
import { AcademicActions } from "Components/Admin/AdminAcademic/AcademicActions";
import { AcademicForm } from "Components/Admin/AdminAcademic/AcademicForm";
import { AcademicTable } from "Components/Admin/AdminAcademic/AcademicTable";
import { Header } from "Components/Admin/StyleComponent/Header";
import { useEffect, useState } from "react";
import {
  useAddAcademicMutation,
  useBulkDeleteAcademicsMutation,
  useDeleteAcademicMutation,
  useGetAllAcademicsQuery,
  useUpdateAcademicMutation,
} from "Services/Admin/AcademicApiSlice";
import { Academic } from "Types/Admin/AdminAcademicType";

const PLANT = process.env.REACT_APP_PLANT;

const AdminAcademic = () => {
  const { data: apiAcademics = [], isLoading } = useGetAllAcademicsQuery({
    plant: PLANT || "",
  });

  const academics: Academic[] = apiAcademics.map((academic: Academic) => ({
    id: academic.id,
    academic: academic.academic,
    isActive: academic.isActive,
    crBy: academic.crBy,
    upBy: academic.upBy,
    createdAt: academic.createdAt,
    updatedAt: academic.updatedAt,
    plant: academic.plant,
  }));

  const [addAcademic] = useAddAcademicMutation();
  const [updateAcademic] = useUpdateAcademicMutation();
  const [deleteAcademic] = useDeleteAcademicMutation();

  const [showForm, setShowForm] = useState(false);
  const [viewMode, setViewMode] = useState(false);
  const [editingAcademic, setEditingAcademic] = useState<Academic | null>(null);
  const [selectedAcademicId, setSelectedAcademicId] = useState<number | null>(
    null,
  );

  const [bulkDeleteAcademics, { isLoading: isBulkDeletingAcademics }] =
  useBulkDeleteAcademicsMutation();
  const [selectedAcademics, setSelectedAcademics] = useState<number[]>([]);

  const [filteredAcademics, setFilteredAcademics] = useState<Academic[]>([]);
  const [filterOption, setFilterOption] = useState("All");
  const [searchText, setSearchText] = useState("");

  const handleTableSelect = (ids: number[]) => {
    setSelectedAcademics(ids);
    setSelectedAcademicId(ids.length > 0 ? ids[0] : null);
  };

    const handleBulkDelete = async () => {
    if (selectedAcademics.length === 0) return;

    try {
      const count = selectedAcademics.length;
      const res = await bulkDeleteAcademics({
        ids: selectedAcademics,
        plant: PLANT || "",
      }).unwrap();

      notifications.show({
        message: res.message,
        color: "teal",
      });

      setSelectedAcademics([]);
      setSelectedAcademicId(null);
    } catch (error: any) {
      let errorMessage = "Failed to delete academics";
      if (error.data?.message) {
        errorMessage = error.data.message;
      } else if (typeof error.data === "string" && error.data) {
        errorMessage = error.data;
      } else if (error.message) {
        errorMessage = error.message;
      }
      notifications.show({
        message: errorMessage,
        color: "red",
      });
      console.error("Failed to bulk delete academics:", error);
    }
  };

  useEffect(() => {
    let filtered = academics ? [...academics] : [];

    if (filterOption === "Active") {
      filtered = filtered.filter((academic) => academic.isActive === "Y");
    } else if (filterOption === "Inactive") {
      filtered = filtered.filter((academic) => academic.isActive !== "Y");
    }

    if (searchText) {
      filtered = filtered.filter((academic) =>
        academic.academic
          .trim()
          .toLowerCase()
          .includes(searchText.trim().toLowerCase()),
      );
    }

    const currentFilteredJSON = JSON.stringify(filtered);
    const previousFilteredJSON = JSON.stringify(filteredAcademics);

    if (currentFilteredJSON !== previousFilteredJSON) {
      setFilteredAcademics(filtered);
    }
  }, [academics, filterOption, searchText]);

  const handleViewAcademic = async (id: number) => {
    try {
      const academicToView = academics.find((academic) => academic.id === id);

      if (academicToView) {
        setEditingAcademic(academicToView);
        setViewMode(true);
        setShowForm(true);
      }
    } catch (error) {
      console.error("Failed to get academic details:", error);
    }
  };

  const handleEditAcademic = async (id: number) => {
    try {
      const academicToEdit = academics.find((academic) => academic.id === id);

      if (academicToEdit) {
        setEditingAcademic(academicToEdit);
        setViewMode(false);
        setShowForm(true);
      }
    } catch (error) {
      console.error("Failed to get academic details:", error);
    }
  };

  const handleSubmit = async (data: { academic: string; isActive: string }) => {
    try {
      if (editingAcademic) {
        const response = await updateAcademic({
          id: editingAcademic.id,
          plant: PLANT || "",
          data: {
            academic: data.academic,
            isActive: data.isActive,
            upBy: "admin",
          },
        }).unwrap();
        notifications.show({
          message: response.message,
          color: "teal",
        });
      } else {
        const addResponse = await addAcademic({
          plant: PLANT || "",
          data: {
            academic: data.academic,
            isActive: data.isActive,
            crBy: "admin",
          },
        }).unwrap();
        notifications.show({
          message: addResponse.message,
          color: "teal",
        });
      }

      setShowForm(false);
      setEditingAcademic(null);
    } catch (error: any) {
      let errorMessage = "Failed to save academic";
      if (error.data?.message) {
        errorMessage = error.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      notifications.show({
        message: errorMessage,
        color: "red",
      });
      console.error("Failed to save academic:", error);
    }

  };

  const handleDelete = async (id?: number) => {
    const academicIdToDelete = id || selectedAcademicId;

    if (academicIdToDelete) {
      try {
        const res = await deleteAcademic({
          id: academicIdToDelete,
          plant: PLANT || "",
        }).unwrap();

        notifications.show({
          message: res.message,
          color: "teal",
        });
        setSelectedAcademicId(null);
      } catch (error: any) {
        let errorMessage = "Failed to delete academic";
        if (error.data?.message) {
          errorMessage = error.data.message;
        } else if (typeof error.data === "string" && error.data) {
          errorMessage = error.data;
        } else if (error.message) {
          errorMessage = error.message;
        }
        notifications.show({
          message: errorMessage,
          color: "red",
        });
        console.error("Failed to delete academic:", error);
      }
    }
  };

  const academicMappingFromDTOToRequest = (
    editingAcademic: Academic | null,
  ) => {
    if (editingAcademic != null) {
      return {
        academic: editingAcademic.academic,
        isActive: editingAcademic.isActive,
      };
    } else {
      return null;
    }
  };
  if (isLoading && !showForm) {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="w-10 h-10 border-4 border-orange-400 border-t-transparent rounded-full animate-spin"></div>
    </div>
  );
}

  return (
    <div className="font-body p-6 mx-auto">
      {!showForm ? (
        <>
          <Header
            title="Academic"
            onAdd={() => setShowForm(true)}
            addLabel="Add Academic"
          />
          <div className="bg-white rounded-lg border shadow-sm overflow-hidden p-4 pt-8">
            <AcademicActions
              hideEdit={false}
              onDelete={handleBulkDelete}
              onFilterChange={setFilterOption}
              onSearchChange={setSearchText}
              isEditDisabled={!selectedAcademicId}
              isDeleteDisabled={!selectedAcademicId}
              isBulkDeleting={isBulkDeletingAcademics}
            />
            <AcademicTable
              academics={filteredAcademics}
              onSelectAcademic={handleTableSelect}
              onDeleteAcademic={(id) => handleDelete(id)}
              onViewAcademic={handleViewAcademic}
              onEditAcademic={handleEditAcademic}
              selectedAcademic={selectedAcademicId}
             
            />
          </div>
        </>
      ) : (
        <>
          <button
            className="flex items-center gap-1 text-gray-500 hover:text-gray-700 mb-4"
            onClick={() => {
              setShowForm(false);
              setEditingAcademic(null);
              setViewMode(false);
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide lucide-arrow-left-icon lucide-arrow-left h-4 w-4"
            >
              <path d="m12 19-7-7 7-7" />
              <path d="M19 12H5" />
            </svg>
            <span className="text-sm">Back</span>
          </button>
          <Header
            title={
              viewMode
                ? "View Academic"
                : editingAcademic
                  ? "Edit Academic"
                  : "Add Academic"
            }
          />
          <AcademicForm
            initialValues={
              academicMappingFromDTOToRequest(editingAcademic) || {
                isActive: "Y",
                academic: "",
              }
            }
            onSubmit={handleSubmit}
            onCancel={() => {
              setShowForm(false);
              setViewMode(false);
              setEditingAcademic(null);
            }}
            mode={viewMode ? "view" : editingAcademic ? "edit" : "add"}
          />
        </>
      )}
    </div>
  );
};

export default AdminAcademic;
